import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHoverIntent } from "./useHoverIntent.js";
import { Bug, Database, Info, Lightbulb, MessageCircle, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { T } from "../app/tokens.js";
import { APP_NAME } from "../constants/branding.js";
import { getRoleDisplayLabel } from "../contexts/SensitivityPolicyContext.jsx";
import { Popover } from "./Popover.jsx";
import { Tooltip } from "./Tooltip.jsx";
import { buildFeedbackPayload, submitFeedback } from "../data/feedbackClient.js";

// Keep the preview rollout behind one nearby switch so it can be demoted or
// disabled without changing the shell or the form itself.
export const SHOW_FEEDBACK_WIDGET = true;

const OBSCURING_OVERLAY = '[aria-modal="true"], [data-feedback-obscuring-overlay="true"]';
const FEEDBACK_PULSE_SESSION_KEY = "feedback-launcher-pulse-shown";

function pulseConsumed() {
  try {
    return window.sessionStorage.getItem(FEEDBACK_PULSE_SESSION_KEY) === "1";
  } catch {
    // If storage is unavailable, avoid a pulse that could recur on every mount.
    return true;
  }
}

function consumePulse() {
  try {
    window.sessionStorage.setItem(FEEDBACK_PULSE_SESSION_KEY, "1");
    return true;
  } catch {
    return false;
  }
}

function hasActiveEditor() {
  return Boolean(document.querySelector(OBSCURING_OVERLAY));
}

const DEFAULT_SEVERITY = "annoying";
const SEVERITIES = [
  { value: "minor", label: "Minor" },
  { value: "annoying", label: "Annoying" },
  { value: "blocking", label: "Blocking" },
];

const SUCCESS_MESSAGES = {
  idea: "Thanks - your idea is logged",
  bug: "Thanks - bug logged, we're on it",
  data_error: "Thanks - data error flagged",
};

export function getFeedbackSuccessMessage(type) {
  return SUCCESS_MESSAGES[type] || "Thanks - it's logged";
}

const SUCCESS_ANIMATION_CSS = `
  @keyframes feedback-check-draw {
    to { stroke-dashoffset: 0; }
  }

  @keyframes feedback-success-pop {
    0% { opacity: 0; transform: scale(.6); }
    60% { transform: scale(1.08); }
    100% { opacity: 1; transform: scale(1); }
  }

  .feedback-success-checkmark circle {
    fill: none;
    stroke: ${T.green};
    stroke-width: 5;
    stroke-dasharray: 170;
    stroke-dashoffset: 170;
    transform-origin: center;
  }

  .feedback-success-checkmark path {
    fill: none;
    stroke: ${T.green};
    stroke-width: 6;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
  }

  .feedback-success-checkmark.is-animated circle {
    animation: feedback-check-draw .5s ease forwards;
  }

  .feedback-success-checkmark.is-animated path {
    animation: feedback-check-draw .35s .4s ease forwards;
  }

  .feedback-success-message.is-animated {
    animation: feedback-success-pop .4s .5s both;
  }

  .feedback-success-checkmark.is-static circle,
  .feedback-success-checkmark.is-static path {
    stroke-dashoffset: 0;
  }

  .feedback-success-message.is-static {
    opacity: 1;
    transform: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .feedback-success-checkmark.is-animated circle,
    .feedback-success-checkmark.is-animated path,
    .feedback-success-message.is-animated {
      animation: none !important;
    }

    .feedback-success-checkmark.is-animated circle,
    .feedback-success-checkmark.is-animated path {
      stroke-dashoffset: 0;
    }

    .feedback-success-message.is-animated {
      opacity: 1;
      transform: none;
    }
  }
`;

const S = {
  shell: {
    position: "fixed",
    right: 22,
    bottom: 20,
    zIndex: 30,
    fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
  },
  trigger: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    boxSizing: "border-box",
    border: "none",
    borderRadius: "50%",
    background: T.blue,
    color: T.white,
    padding: 0,
    boxShadow: T.shadowFloat,
    cursor: "pointer",
    fontSize: T.fsBase,
    fontWeight: T.fwBold,
  },
  panel: {
    width: 340,
    height: 430,
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 32px)",
    padding: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    background: T.white,
    color: T.dark,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 18px",
    background: T.policyBandBg,
    borderBottom: `1px solid ${T.policyBandBorder}`,
    color: T.dark,
  },
  subtext: {
    margin: "4px 0 0",
    color: T.mid,
    fontSize: T.fsSm,
    lineHeight: 1.4,
  },
  close: {
    display: "grid",
    placeItems: "center",
    width: 28,
    height: 28,
    flexShrink: 0,
    border: "none",
    borderRadius: 7,
    background: "transparent",
    color: T.mid,
    cursor: "pointer",
  },
  body: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
    padding: 18,
    background: T.white,
  },
  segmented: {
    display: "grid",
    gridTemplateColumns: "1fr 1.2fr 1fr",
    gap: 3,
    padding: 3,
    marginBottom: 14,
    borderRadius: 9,
    background: T.lightGray,
  },
  segment: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    border: "none",
    borderRadius: 7,
    padding: "7px 10px",
    background: "transparent",
    color: T.mid,
    cursor: "pointer",
    fontSize: T.fsSm5,
    fontWeight: T.fwSemibold,
  },
  label: {
    display: "block",
    marginBottom: 6,
    color: T.dark,
    fontSize: T.fsSm,
    fontWeight: T.fwSemibold,
  },
  severityRow: {
    display: "flex",
    gap: 6,
    marginBottom: 12,
  },
  severity: {
    flex: 1,
    border: `1px solid ${T.border}`,
    borderRadius: 7,
    padding: "6px 4px",
    background: T.white,
    color: T.mid,
    cursor: "pointer",
    fontSize: T.fsXs,
    fontWeight: T.fwSemibold,
  },
  textarea: {
    display: "block",
    width: "100%",
    minHeight: 0,
    flex: 1,
    boxSizing: "border-box",
    resize: "none",
    border: `1px solid ${T.borderDark}`,
    borderRadius: 8,
    padding: "9px 10px",
    color: T.dark,
    background: T.white,
    font: "inherit",
    fontSize: T.fsSm5,
    lineHeight: 1.45,
  },
  context: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    marginTop: 11,
    color: T.mid,
    fontSize: T.fsXxs5,
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  dataContext: {
    display: "block",
    marginTop: 10,
    padding: "9px 11px",
    border: `1px solid ${T.blueSoftBorder}`,
    borderRadius: 9,
    background: T.blueSoft,
  },
  capturedValues: {
    minWidth: 0,
    overflow: "hidden",
    color: T.light,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  capturedIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 13,
    height: 13,
    flex: "0 0 13px",
    border: `1px solid ${T.borderDark}`,
    borderRadius: "50%",
    color: T.light,
    fontSize: T.fsMin5,
    fontWeight: T.fwBold,
  },
  form: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginTop: 12,
  },
  from: {
    minWidth: 0,
    overflow: "hidden",
    color: T.mid,
    fontSize: T.fsXs,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  send: {
    flexShrink: 0,
    border: "none",
    borderRadius: 7,
    padding: "8px 15px",
    background: T.blue,
    color: T.white,
    cursor: "pointer",
    fontSize: T.fsSm5,
    fontWeight: T.fwBold,
  },
  error: {
    marginTop: 9,
    color: T.ragRed,
    fontSize: T.fsXs5,
    lineHeight: 1.4,
  },
  success: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "15px 12px 17px",
    color: T.dark,
    fontSize: T.fsBase,
    fontWeight: T.fwBold,
    textAlign: "center",
  },
  checkmark: {
    width: 56,
    height: 56,
    flex: "0 0 56px",
  },
};

function currentRoute({ pathname = "", search = "" } = {}) {
  return `${pathname || "/"}${search || ""}`;
}

export function deriveFeedbackRecordRef(pathname = "") {
  const patterns = [
    [/^\/offices\/([^/]+)/, "office"],
    [/^\/leases\/([^/]+)/, "lease"],
    [/^\/projects\/([^/]+)/, "project"],
    [/^\/markets\/(?!plan(?:\/|$)|strategy(?:\/|$)|pressure(?:\/|$)|scenario(?:\/|$)|guidance(?:\/|$))([^/]+)/, "market"],
    [/^\/admin\/data\/import\/([^/]+)/, "import"],
    [/^\/imports\/([^/]+)/, "import"],
  ];
  for (const [pattern, kind] of patterns) {
    const match = pathname.match(pattern);
    if (match?.[1]) return `${kind}:${decodeURIComponent(match[1])}`;
  }
  return null;
}

export function captureFeedbackContext({
  pathname = typeof window !== "undefined" ? window.location.pathname : "/",
  search = typeof window !== "undefined" ? window.location.search : "",
  width = typeof window !== "undefined" ? window.innerWidth : null,
  height = typeof window !== "undefined" ? window.innerHeight : null,
} = {}) {
  return {
    pageRoute: currentRoute({ pathname, search }),
    recordRef: deriveFeedbackRecordRef(pathname),
    appBuild: null,
    viewport: Number.isFinite(width) && Number.isFinite(height) ? `${width} x ${height}` : null,
  };
}

function formatRecordLabel(recordRef) {
  if (!recordRef) return null;
  const [kind, ...rest] = recordRef.split(":");
  const label = {
    office: "Office",
    lease: "Lease",
    project: "Project",
    market: "Market",
    import: "Import",
  }[kind] || kind;
  return `${label} - ${rest.join(":")}`;
}

function getPrefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function formatCapturedContext({ pageRoute, roleLabel, viewport } = {}) {
  return `Captured: ${pageRoute || "Current page"} · ${roleLabel || "Role unavailable"} · viewport ${viewport || "Not available"}`;
}

function CapturedContext({ context, roleLabel, testId = "feedback-captured-context" }) {
  const captured = formatCapturedContext({
    pageRoute: context.pageRoute,
    roleLabel,
    viewport: context.viewport,
  });
  const [prefix, ...rest] = captured.split(" · ");

  return (
    <div
      data-testid={testId}
      aria-label="Attached page context"
      style={S.context}
    >
      <span style={S.capturedIcon} aria-hidden="true">
        <Info size={9} strokeWidth={2} />
      </span>
      <span>
        {prefix} ·{" "}
        <span style={S.capturedValues}>{rest.join(" · ")}</span>
      </span>
    </div>
  );
}

function ContextChip({ context, roleLabel, type }) {
  if (type === "data_error") {
    const recordLabel = formatRecordLabel(context.recordRef);
    return (
      <>
        <div
          data-testid="feedback-context"
          aria-label="Which record"
          style={{ ...S.context, ...S.dataContext }}
        >
          <div style={{ color: T.blue, fontWeight: T.fwBold }}>
            <Database size={13} strokeWidth={2} aria-hidden="true" style={{ verticalAlign: "text-bottom", marginRight: 5 }} />
            Which record
          </div>
          <div style={{ marginTop: 2, color: recordLabel ? T.blue : T.amber, fontWeight: T.fwSemibold }}>
            {recordLabel || "No record in view - name it in your note"}
          </div>
          {recordLabel && (
            <div style={{ marginTop: 3, color: T.mid }}>
              Pulled from what you&apos;re viewing - this tells us exactly what to correct.
            </div>
          )}
        </div>
        <CapturedContext context={context} roleLabel={roleLabel} />
      </>
    );
  }

  return (
    <CapturedContext
      context={context}
      roleLabel={roleLabel}
      testId="feedback-context"
    />
  );
}

export function FeedbackWidget({ currentUser, currentRole }) {
  const routerLocation = useLocation();
  const triggerRef = useRef(null);
  const textareaRef = useRef(null);
  const [open, setOpen] = useState(false);
  const tooltipIntent = useHoverIntent({ enabled: !open });
  const tooltipVisible = tooltipIntent.open;
  const setTooltipVisible = tooltipIntent.dismiss;
  const [type, setType] = useState("bug");
  const [severity, setSeverity] = useState(DEFAULT_SEVERITY);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submittedType, setSubmittedType] = useState(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getPrefersReducedMotion);
  const [editorOpen, setEditorOpen] = useState(hasActiveEditor);
  const [pulsing, setPulsing] = useState(false);
  const editorOpenRef = useRef(editorOpen);

  const context = useMemo(
    () => captureFeedbackContext({
      pathname: routerLocation.pathname,
      search: routerLocation.search,
    }),
    [routerLocation.pathname, routerLocation.search],
  );
  const roleLabel = getRoleDisplayLabel(currentRole);
  const email = currentUser?.email || "your account";

  useEffect(() => {
    if (open && !submitted) textareaRef.current?.focus();
  }, [open, submitted]);

  useEffect(() => {
    // Editors can be portaled outside the shell. Observe DOM presence rather
    // than route changes so closing a drawer restores the launcher immediately.
    const sync = () => {
      const active = hasActiveEditor();
      if (editorOpenRef.current !== active) {
        editorOpenRef.current = active;
        setEditorOpen(active);
        if (active) setTooltipVisible(false);
      }
    };
    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-modal", "data-feedback-obscuring-overlay"],
    });
    sync();
    return () => observer.disconnect();
  }, [setTooltipVisible]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", updatePreference);
    else mediaQuery.addListener?.(updatePreference);
    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", updatePreference);
      else mediaQuery.removeListener?.(updatePreference);
    };
  }, []);

  useEffect(() => {
    if (editorOpen || prefersReducedMotion || pulseConsumed()) {
      setPulsing(false);
      return undefined;
    }
    let finishTimer;
    const startTimer = window.setTimeout(() => {
      // Recheck at start: another mount or a changed system preference may
      // have consumed or invalidated the scheduled effect.
      if (getPrefersReducedMotion() || hasActiveEditor() || pulseConsumed() || !consumePulse()) return;
      setPulsing(true);
      finishTimer = window.setTimeout(() => setPulsing(false), 1300);
    }, 1100);
    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(finishTimer);
    };
  }, [editorOpen, prefersReducedMotion]);

  function close() {
    setOpen(false);
    setError("");
  }

  useEffect(() => {
    if (editorOpen && open) close();
  // close only needs to run on the transition into an editor.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen]);

  function handleTypeChange(nextType) {
    setType(nextType);
    setSeverity(nextType === "bug" ? DEFAULT_SEVERITY : null);
    setError("");
    setSubmitted(false);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    setError("");
    let payload;
    try {
      payload = buildFeedbackPayload({
        type,
        severity,
        body,
        ...context,
      });
    } catch (err) {
      setError(err.message || "Enter feedback before sending.");
      return;
    }

    setSubmitting(true);
    try {
      await submitFeedback(payload);
      setSubmittedType(type);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Could not send feedback. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!SHOW_FEEDBACK_WIDGET) return null;

  return (
    <div style={{ ...S.shell, ...(editorOpen ? { display: "none" } : null) }} data-testid="feedback-widget">
      <div className="feedback-trigger-entrance">
        <button
        ref={triggerRef}
        type="button"
        className={`feedback-trigger${pulsing ? " is-pulsing" : ""}`}
        data-testid="feedback-trigger"
        aria-label="Feedback"
        aria-expanded={open}
        {...tooltipIntent.triggerProps}
        // The shared Popover dismisses on document mousedown. Let this
        // button's click own toggling so that dismissal cannot reopen it.
        onMouseDown={(event) => {
          tooltipIntent.triggerProps.onMouseDown(event);
          if (open) event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") setTooltipVisible(false);
        }}
        onClick={() => {
          setTooltipVisible(false);
          if (open) close();
          else {
            setOpen(true);
            setSubmitted(false);
          }
        }}
        style={{
          ...S.trigger,
          "--feedback-hover-shadow": T.shadowFeedbackHover,
          "--feedback-pulse-ring": T.blueSoftBorder,
        }}
      >
        <MessageCircle size={17} aria-hidden="true" />
      </button>
        {tooltipVisible && !open && !editorOpen && (
          <Tooltip style={{ right: 0, bottom: "calc(100% + 10px)" }}>
            Feedback
          </Tooltip>
        )}
      </div>

      {open && (
        <Popover
          data-testid="feedback-popover"
          role="dialog"
          aria-labelledby="feedback-title"
          variant="menu"
          triggerRef={triggerRef}
          onClose={close}
          style={{
            position: "absolute",
            right: 0,
            bottom: "calc(100% + 10px)",
            zIndex: 30,
            border: `1px solid ${T.borderDark}`,
            borderRadius: 16,
            overflow: "hidden",
            boxShadow: T.shadowMega,
          }}
        >
          <div style={S.panel} data-testid="feedback-panel">
            <div style={S.header}>
              <div>
                <h2 id="feedback-title" style={{ margin: 0, color: T.dark, fontSize: T.fsMd5, fontWeight: T.fwBold }}>
                  Share feedback
                </h2>
                <p style={S.subtext}>Help us improve {APP_NAME}.</p>
              </div>
              <button type="button" aria-label="Close feedback form" onClick={close} style={S.close}>
                <X size={17} aria-hidden="true" />
              </button>
            </div>

             <div style={S.body}>
               {submitted && <style>{SUCCESS_ANIMATION_CSS}</style>}
               {submitted ? (
                 <div
                   data-testid="feedback-success"
                   role="status"
                   aria-live="polite"
                   style={S.success}
                 >
                   <svg
                     className={`feedback-success-checkmark ${prefersReducedMotion ? "is-static" : "is-animated"}`}
                     data-testid="feedback-success-checkmark"
                     viewBox="0 0 60 60"
                     aria-hidden="true"
                     style={S.checkmark}
                   >
                     <circle cx="30" cy="30" r="27" />
                     <path d="M18 31 l8 8 l16 -18" />
                   </svg>
                   <div
                     className={`feedback-success-message ${prefersReducedMotion ? "is-static" : "is-animated"}`}
                     data-testid="feedback-success-message"
                   >
                     {getFeedbackSuccessMessage(submittedType)}
                   </div>
                 </div>
               ) : (
               <form onSubmit={handleSubmit} style={S.form}>
                <div role="group" aria-label="Feedback type" style={S.segmented}>
                    {["bug", "data_error", "idea"].map((option) => (
                    <button
                      key={option}
                      type="button"
                      data-testid={`feedback-type-${option}`}
                        aria-label={`${option === "data_error" ? "Data error" : option[0].toUpperCase() + option.slice(1)} feedback`}
                      aria-pressed={type === option}
                      onClick={() => handleTypeChange(option)}
                      style={{
                        ...S.segment,
                        ...(type === option ? { background: T.white, color: T.blue, boxShadow: T.shadowCardSm } : null),
                      }}
                    >
                      {option === "bug" && <Bug size={14} strokeWidth={1.8} aria-hidden="true" />}
                      {option === "data_error" && <Database size={14} strokeWidth={1.8} aria-hidden="true" />}
                      {option === "idea" && <Lightbulb size={14} strokeWidth={1.8} aria-hidden="true" />}
                      {option === "data_error" ? "Data error" : option === "bug" ? "Bug" : "Idea"}
                    </button>
                  ))}
                </div>

                {type === "bug" && (
                  <div role="group" aria-label="Bug severity" style={S.severityRow}>
                    {SEVERITIES.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        data-testid={`feedback-severity-${option.value}`}
                        aria-label={`${option.label} severity`}
                        aria-pressed={severity === option.value}
                        onClick={() => setSeverity(option.value)}
                        style={{
                          ...S.severity,
                          ...(severity === option.value ? { borderColor: T.blue, background: T.blueSoft, color: T.blue } : null),
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}

                <label htmlFor="feedback-body" style={S.label}>
                  {type === "data_error"
                    ? "What's wrong, and what should it be?"
                    : type === "idea"
                    ? "What's your idea?"
                    : "What happened?"}
                </label>
                <textarea
                  ref={textareaRef}
                  id="feedback-body"
                  data-testid="feedback-body"
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  placeholder={type === "data_error"
                    ? "e.g. 315 Montgomery shows 3 leases but should be 2"
                    : type === "bug"
                    ? "What went wrong, and what were you doing when it happened?"
                    : "What would make this better? What are you trying to do?"}
                  aria-label="Feedback details"
                  maxLength={4000}
                  style={S.textarea}
                />
                <ContextChip context={context} roleLabel={roleLabel} type={type} />
                {error && <div role="alert" data-testid="feedback-error" style={S.error}>{error}</div>}
                <div style={S.footer}>
                  <span style={S.from}>From {email}</span>
                  <button
                    type="submit"
                    data-testid="feedback-send"
                    disabled={submitting}
                    style={{ ...S.send, ...(submitting ? { opacity: 0.65, cursor: "wait" } : null) }}
                  >
                    {submitting ? "Sending…" : "Send"}
                  </button>
                </div>
               </form>
               )}
             </div>
          </div>
        </Popover>
      )}
    </div>
  );
}